import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  X, 
  Search, 
  FileText, 
  Building2, 
  User, 
  Plus,
  CheckCircle,
  Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function TemplateLibrary({ isOpen, onClose, onSelectTemplate, language }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("cabinet"); // cabinet | personal

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, activeTab]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          show_cabinet_wide: activeTab === "cabinet",
          show_personal: activeTab === "personal"
        }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      if (error.response?.status && error.response.status !== 200) {
        toast.error(language === "fr" ? "Erreur lors du chargement des templates" : "Error loading templates");
      }
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedText = (text) => {
    if (typeof text === 'object' && text !== null) {
      return text[language] || text['fr'] || text['en'] || Object.values(text)[0];
    }
    return text;
  };

  const filteredTemplates = templates.filter(template => {
    const name = getLocalizedText(template.name).toLowerCase();
    const description = getLocalizedText(template.description).toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || description.includes(query);
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <Card className="w-full max-w-4xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">
                    {language === "fr" ? "Bibliothèque de Templates" : "Template Library"}
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    {language === "fr" 
                      ? "Sélectionnez un template pour démarrer rapidement"
                      : "Select a template to get started quickly"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-lg transition"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 px-6 pt-4 border-b">
                <button
                  onClick={() => setActiveTab("cabinet")}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                    activeTab === "cabinet"
                      ? "bg-white text-blue-600 border-b-2 border-blue-600"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Building2 className="h-4 w-4 inline mr-2" />
                  {language === "fr" ? "Cabinet" : "Firm"}
                </button>
                <button
                  onClick={() => setActiveTab("personal")}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                    activeTab === "personal"
                      ? "bg-white text-blue-600 border-b-2 border-blue-600"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <User className="h-4 w-4 inline mr-2" />
                  {language === "fr" ? "Personnel" : "Personal"}
                </button>
              </div>

              {/* Search */}
              <div className="p-6 pb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === "fr" ? "Rechercher un template..." : "Search templates..."}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Templates Grid */}
              <div className="px-6 pb-6 overflow-y-auto max-h-[50vh]">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                    <FileText className="h-12 w-12 mb-3 opacity-50" />
                    <p className="text-sm">
                      {language === "fr" 
                        ? "Aucun template trouvé" 
                        : "No templates found"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplates.map((template, index) => (
                      <motion.div
                        key={template.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <button
                          onClick={() => {
                            onSelectTemplate(template);
                            onClose();
                          }}
                          className="w-full text-left p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-md transition group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="p-2 bg-blue-50 rounded-lg">
                                <FileText className="h-4 w-4 text-blue-600" />
                              </div>
                              {template.is_cabinet_wide && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  {language === "fr" ? "Cabinet" : "Firm"}
                                </Badge>
                              )}
                              {template.is_approved ? (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  {language === "fr" ? "Validé" : "Approved"}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {language === "fr" ? "En attente" : "Pending"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition">
                            {getLocalizedText(template.name)}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2">
                            {getLocalizedText(template.description)}
                          </p>
                          <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                            <span>{template.clauses?.length || 0} {language === "fr" ? "clauses" : "clauses"}</span>
                            <span>•</span>
                            <span>{template.required_variables?.length || 0} {language === "fr" ? "variables" : "variables"}</span>
                          </div>
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t bg-slate-50">
                <p className="text-sm text-slate-600">
                  {filteredTemplates.length} {language === "fr" ? "template(s)" : "template(s)"}
                </p>
                <Button onClick={onClose} variant="outline">
                  {language === "fr" ? "Fermer" : "Close"}
                </Button>
              </div>
            </Card>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
